import { tryGetDirectory, readTextFiles, writeFile } from "../../storage/fsa.ts";
import { writeModuleIndex } from "../shared/module-index.ts";
import { escapeHtml } from "./sanitize-html.ts";
import { chatIndexPath, chatMessagesDirPath } from "./paths.ts";
import type { StoredMessage } from "./stored-message.ts";
import type { Chat } from "./types.ts";

/**
 * Renders `teams/<chat-key>/index.html` from the message JSON files already
 * on disk — never from anything held in memory.
 *
 * This is deliberately a separate, idempotent, network-free pass rather than
 * something done inline while fetching messages. Aggregating "all messages in
 * this chat" doesn't fit the one-item-at-a-time resume model (§7): if writing
 * a combined index.html were the unit of work, an interruption partway
 * through a chat would leave no single clean checkpoint. Instead each message
 * is its own resumable item (a JSON file, exactly like mail's .eml), and
 * index.html is regenerated from whatever is on disk — safe to re-run after
 * every resume, and safe to re-run on a chat that turns out to already be
 * complete.
 */

function formatTimestamp(iso: string): string {
    try {
        return new Date(iso).toLocaleString();
    } catch {
        return iso;
    }
}

async function loadMessages(
    root: FileSystemDirectoryHandle,
    key: string
): Promise<StoredMessage[]> {
    const dir = await tryGetDirectory(root, chatMessagesDirPath(key).split("/"));
    if (!dir) {
        return [];
    }

    const messages: StoredMessage[] = [];
    for await (const { name, text } of readTextFiles(dir)) {
        if (!name.endsWith(".json")) {
            continue;
        }
        try {
            messages.push(JSON.parse(text) as StoredMessage);
        } catch {
            // Skip a corrupt file rather than fail the whole chat's render —
            // it will simply be missing from this transcript. It stays on
            // disk, so it is at least visible via export.log or manual review.
        }
    }

    messages.sort((a, b) => a.createdDateTime.localeCompare(b.createdDateTime));
    return messages;
}

function renderPage(chat: Chat, chatLabel: string, messages: StoredMessage[]): string {
    const rows = messages
        .map(
            (m) => `
        <article class="msg">
            <header>
                <span class="from">${escapeHtml(m.from)}</span>
                <time datetime="${escapeHtml(m.createdDateTime)}">${escapeHtml(
                formatTimestamp(m.createdDateTime)
            )}</time>
            </header>
            <div class="body">${m.html}</div>
        </article>`
        )
        .join("\n");

    return `<!doctype html>
<html lang="en">
<head>
<meta charset="UTF-8">
<meta name="viewport" content="width=device-width, initial-scale=1.0">
<title>${escapeHtml(chatLabel)}</title>
<style>
    body { font: 15px/1.5 system-ui, -apple-system, "Segoe UI", sans-serif; max-width: 46rem; margin: 0 auto; padding: 1.5rem 1rem 4rem; color: #1b1f24; }
    h1 { font-size: 1.3rem; }
    .meta { color: #5b6570; font-size: 0.85rem; margin-bottom: 1.5rem; }
    .msg { border-top: 1px solid #d8dce2; padding: 0.75rem 0; }
    .msg header { display: flex; justify-content: space-between; gap: 1rem; font-size: 0.82rem; color: #5b6570; margin-bottom: 0.25rem; }
    .msg .from { font-weight: 600; color: #1b1f24; }
    .msg img { max-width: 100%; border-radius: 4px; }
    @media (prefers-color-scheme: dark) {
        body { background: #16191d; color: #e6e9ed; }
        .meta { color: #9aa4b0; }
        .msg { border-color: #333940; }
        .msg header { color: #9aa4b0; }
        .msg .from { color: #e6e9ed; }
    }
</style>
</head>
<body>
<h1>${escapeHtml(chatLabel)}</h1>
<p class="meta">${messages.length} message${messages.length === 1 ? "" : "s"} · ${escapeHtml(
        chat.chatType
    )}</p>
${rows || `<p class="meta">No messages exported yet.</p>`}
</body>
</html>
`;
}

function countImages(html: string): number {
    return (html.match(/<img[\s>]/gi) ?? []).length;
}

/** One chat's stats, as needed by the global teams/index.html (renderTeamsIndex). */
export interface ChatIndexSummary {
    key: string;
    label: string;
    chatType: string;
    messageCount: number;
    imageCount: number;
    firstActivity: string | undefined;
    lastActivity: string | undefined;
}

/**
 * Regenerates one chat's index.html from disk. Safe to call any number of
 * times — including on a chat with zero messages fetched so far, or one whose
 * fetch is still in progress on a later resume.
 *
 * Returns the same stats it just rendered so the caller can accumulate them
 * for the global index (renderTeamsIndex) without a second disk read — every
 * chat already goes through this function once per run regardless.
 */
export async function renderChatIndex(
    root: FileSystemDirectoryHandle,
    key: string,
    chat: Chat,
    chatLabel: string
): Promise<ChatIndexSummary> {
    const messages = await loadMessages(root, key);
    const html = renderPage(chat, chatLabel, messages);
    await writeFile(root, chatIndexPath(key), html);

    let imageCount = 0;
    for (const m of messages) {
        imageCount += countImages(m.html);
    }

    return {
        key,
        label: chatLabel,
        chatType: chat.chatType,
        messageCount: messages.length,
        imageCount,
        firstActivity: messages[0]?.createdDateTime,
        lastActivity: messages[messages.length - 1]?.createdDateTime
    };
}

/**
 * Rebuilds `teams/index.html` — one row per chat, linking to its own
 * index.html — from the summaries `renderChatIndex` already produced this
 * run. Idempotent and network-free, the same pattern as every other render
 * pass in this codebase: safe to re-run, safe on a chat list that turns out
 * to be unchanged.
 */
export async function renderTeamsIndex(
    root: FileSystemDirectoryHandle,
    summaries: readonly ChatIndexSummary[]
): Promise<void> {
    const sorted = [...summaries].sort((a, b) => a.label.localeCompare(b.label));

    await writeModuleIndex(root, {
        moduleId: "teams",
        itemKind: "html",
        title: "Teams conversations",
        subtitle: `${sorted.length} conversation${sorted.length === 1 ? "" : "s"} exported`,
        columns: [
            { key: "conversation", label: "Conversation" },
            { key: "type", label: "Type" },
            { key: "messages", label: "Messages", numeric: true },
            { key: "images", label: "Images", numeric: true },
            { key: "first", label: "First activity" },
            { key: "last", label: "Last activity" }
        ],
        rows: sorted.map((s) => ({
            cells: {
                conversation: s.label,
                type: s.chatType,
                messages: String(s.messageCount),
                images: String(s.imageCount),
                first: s.firstActivity ? formatTimestamp(s.firstActivity) : "",
                last: s.lastActivity ? formatTimestamp(s.lastActivity) : ""
            },
            sortValues: {
                messages: s.messageCount,
                images: s.imageCount,
                first: s.firstActivity ?? "",
                last: s.lastActivity ?? ""
            },
            href: `${s.key}/index.html`
        })),
        emptyMessage: "No conversations exported yet.",
        searchPlaceholder: "Filter conversations…"
    });
}
